import * as THREE from 'three';

export const PHOTON_PHYSICAL_CONFIG = {
	solarConstantWm2: 1361,
	clearSkyTransmittance: 0.7,
	airMassExponent: 0.678,
	diffuseHorizontalRatio: 0.12,
	minimumProjectedAreaM2: 1,
	luminousEfficacyLmPerW: 110,
} as const;

export interface PhotonPhysicalContext {
	date?: Date;
	timeString?: string;
	latitude?: number | null;
	longitude?: number | null;
	atmosphericClarity?: number;
	cloudCover?: number;
}

export interface SolarIrradianceModel {
	dayOfYear: number;
	elevationDeg: number;
	airMass: number;
	extraterrestrialNormalIrradianceWm2: number;
	directNormalIrradianceWm2: number;
	directHorizontalIrradianceWm2: number;
	diffuseHorizontalIrradianceWm2: number;
	globalHorizontalIrradianceWm2: number;
}

export const computePolygonAreaXY = (polygon: THREE.Vector3[]): number => {
	if (polygon.length < 3) return 0;
	let area = 0;
	for (let i = 0; i < polygon.length; i++) {
		const j = (i + 1) % polygon.length;
		area += polygon[i].x * polygon[j].y - polygon[j].x * polygon[i].y;
	}
	return Math.abs(area) * 0.5;
};

export const computeSunElevationFactor = (sunPosition: THREE.Vector3): number => {
	const lengthSq = sunPosition.lengthSq();
	if (lengthSq <= 1e-12) return 0;
	const sunDir = sunPosition.clone().normalize();
	return Math.max(0, sunDir.z);
};

const computeDayOfYear = (date: Date): number => {
	const start = new Date(date.getFullYear(), 0, 0);
	const diff = date.getTime() - start.getTime();
	return Math.floor(diff / (1000 * 60 * 60 * 24));
};

const computeRelativeAirMass = (elevationDeg: number): number => {
	if (elevationDeg <= 0) return Number.POSITIVE_INFINITY;
	return (
		1 /
		(Math.sin(THREE.MathUtils.degToRad(elevationDeg)) +
			0.50572 * Math.pow(elevationDeg + 6.07995, -1.6364))
	);
};



export const computeClearSkySolarIrradiance = (
	sunPosition: THREE.Vector3,
	physicalContext?: PhotonPhysicalContext
): SolarIrradianceModel => {
	const referenceDate = physicalContext?.date ?? new Date();
	const atmosphericClarity = Math.max(
		0,
		Math.min(1, physicalContext?.atmosphericClarity ?? 1)
	);
	const cloudCover = Math.max(0, Math.min(1, physicalContext?.cloudCover ?? 0));
	const dayOfYear = computeDayOfYear(referenceDate);
	const orbitalFactor = 1 + 0.033 * Math.cos((2 * Math.PI * dayOfYear) / 365);
	const extraterrestrialNormalIrradianceWm2 =
		PHOTON_PHYSICAL_CONFIG.solarConstantWm2 * orbitalFactor;
	
	// Use scene sun vector as source of truth for elevation angle
	const elevationDeg = THREE.MathUtils.radToDeg(
		Math.asin(computeSunElevationFactor(sunPosition))
	);
	
	if (elevationDeg <= 0) {
		return {
			dayOfYear,
			elevationDeg,
			airMass: Number.POSITIVE_INFINITY,
			extraterrestrialNormalIrradianceWm2,
			directNormalIrradianceWm2: 0,
			directHorizontalIrradianceWm2: 0,
			diffuseHorizontalIrradianceWm2: 0,
			globalHorizontalIrradianceWm2: 0,
		};
	}

	const airMass = computeRelativeAirMass(elevationDeg);
	const adjustedTransmittance = Math.max(
		0.1,
		Math.min(0.95, PHOTON_PHYSICAL_CONFIG.clearSkyTransmittance * (0.2 + atmosphericClarity * 1.3))
	);
	const directNormalIrradianceWm2 =
		extraterrestrialNormalIrradianceWm2 *
		Math.pow(
			adjustedTransmittance,
			Math.pow(airMass, PHOTON_PHYSICAL_CONFIG.airMassExponent)
		);
	const elevationRad = THREE.MathUtils.degToRad(elevationDeg);
	const clearSkyDirectHorizontalIrradianceWm2 =
		directNormalIrradianceWm2 * Math.max(0, Math.sin(elevationRad));
	const cloudDirectAttenuation = 1 - 0.75 * cloudCover;
	const directHorizontalIrradianceWm2 =
		clearSkyDirectHorizontalIrradianceWm2 * Math.max(0.1, cloudDirectAttenuation);
	const diffuseRatio =
		PHOTON_PHYSICAL_CONFIG.diffuseHorizontalRatio +
		cloudCover * 0.55 +
		(1 - atmosphericClarity) * 0.15;
	const diffuseHorizontalIrradianceWm2 =
		directHorizontalIrradianceWm2 * Math.max(0.05, diffuseRatio);

	return {
		dayOfYear,
		elevationDeg,
		airMass,
		extraterrestrialNormalIrradianceWm2,
		directNormalIrradianceWm2,
		directHorizontalIrradianceWm2,
		diffuseHorizontalIrradianceWm2,
		globalHorizontalIrradianceWm2:
			directHorizontalIrradianceWm2 + diffuseHorizontalIrradianceWm2,
	};
};
